import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { UserSessionRequest } from '../models/requests/UserSessionRequest';
import { UserSessionResponse } from '../models/responses/UserSessionResponse';
import { addUserSession, deleteUserSession, getUserSessions, updateUserSession, updateUserSessionCollection } from '../api/userSessionApi';
import { useAuth } from '../customAuth/authStateHook';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { SESSION_TITLE_UPDATED_SUCCESS } from '../redux/actions/assistantActions';

interface SessionContextType {
    sessions: UserSessionResponse[];
    currentSessionId: string | null;
    currentSessionCollectionId: number | null;
    isSessionSidebarOpen: boolean;
    isLoading: boolean;
    setCurrentSessionId: (id: string) => void;
    setCurrentSessionCollectionId: (collectionId: number | null) => void;
    toggleSessionSidebar: () => void;
    createNewSession: (title?: string) => Promise<string | null>;
    updateSession: (sessionId: string, name: string) => Promise<void>;
    updateSessionCollectionId: (sessionId: string, collectionId: number) => Promise<void>;
    deleteSession: (id: string) => void;
    refreshSessions: () => Promise<void>;
    markSessionAsUsed: (sessionId: string) => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = () => {
    const context = useContext(SessionContext);
    if (context === undefined) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};

interface SessionProviderProps {
    children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
    const { user, isTokenSynced } = useAuth();
    const [sessions, setSessions] = useState<UserSessionResponse[]>([]);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [isSessionSidebarOpen, setIsSessionSidebarOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasUserSelectedSession, setHasUserSelectedSession] = useState(false);
    const [userSelectedSessionId, setUserSelectedSessionId] = useState<string | null>(null);
    const [currentSessionCollectionId, setCurrentSessionCollectionId] = useState<number | null>(null);
    const [sessionUsageOrder, setSessionUsageOrder] = useState<string[]>(() => {
        const saved = localStorage.getItem('sessionUsageOrder');
        return saved ? JSON.parse(saved) : [];
    });
    const [sessionsWithMessages, setSessionsWithMessages] = useState<Set<string>>(new Set());

    const lastAction = useSelector((state: any) => state.lastAction);

    useEffect(() => {
        localStorage.setItem('sessionUsageOrder', JSON.stringify(sessionUsageOrder));
    }, [sessionUsageOrder]);

    useEffect(() => {
        const reorderAfterUsageChange = () => {
            if (sessions.length > 0) {
                const reorderedSessions = reorderSessionsEfficiently(sessions);
                setSessions(reorderedSessions);
            }
        };

        reorderAfterUsageChange();
    }, [sessionUsageOrder, sessionsWithMessages]);

    useEffect(() => {
        if (lastAction?.type === SESSION_TITLE_UPDATED_SUCCESS) {
            const { sessionId, title } = lastAction.payload;

            setSessions(prev => {
                const updated = prev.map(session =>
                    session.SessionId === sessionId
                        ? { ...session, Name: title }
                        : session
                );
                return updated;
            });
        }
    }, [lastAction]);

    useEffect(() => {
        if (user?.email && isTokenSynced) {
            loadUserSessions();
        } else {
            setSessions([]);
            setCurrentSessionId(null);
            setCurrentSessionCollectionId(null);
            setHasUserSelectedSession(false);
            setUserSelectedSessionId(null);
            setCurrentSessionCollectionId(null);
            setSessionUsageOrder([]);
            setSessionsWithMessages(new Set());
        }
    }, [user?.email, isTokenSynced]);

    const sessionLikelyHasMessages = (session: UserSessionResponse): boolean => {
        if (sessionsWithMessages.has(session.SessionId)) {
            return true;
        }

        const created = new Date(session.DateCreatedUtc).getTime();
        const lastQueried = new Date(session.DateLastTimeQueriedUtc).getTime();
        return lastQueried > created + 1000;
    };

    const reorderSessionsEfficiently = (sessionsToOrder: UserSessionResponse[]): UserSessionResponse[] => {
        const emptySessions: UserSessionResponse[] = [];
        const sessionsWithMessagesArray: UserSessionResponse[] = [];

        for (const session of sessionsToOrder) {
            if (sessionLikelyHasMessages(session)) {
                sessionsWithMessagesArray.push(session);
            } else {
                emptySessions.push(session);
            }
        }

        sessionsWithMessagesArray.sort((a, b) => {
            const aIndex = sessionUsageOrder.indexOf(a.SessionId);
            const bIndex = sessionUsageOrder.indexOf(b.SessionId);

            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;

            const aLastQueried = new Date(a.DateLastTimeQueriedUtc).getTime();
            const bLastQueried = new Date(b.DateLastTimeQueriedUtc).getTime();
            return bLastQueried - aLastQueried;
        });

        emptySessions.sort((a, b) =>
            new Date(b.DateCreatedUtc).getTime() - new Date(a.DateCreatedUtc).getTime()
        );

        return [...emptySessions, ...sessionsWithMessagesArray];
    };

    const loadUserSessions = async () => {
        if (!user?.email) return;

        setIsLoading(true);
        try {
            const response = await getUserSessions(user.email);

            if (response?.data?.result && Array.isArray(response.data.result)) {
                const userSessions: UserSessionResponse[] = response.data.result.map((session: any) => ({
                    Name: session.Name,
                    UserEmail: session.UserEmail,
                    SessionId: session.SessionId,
                    DateCreatedUtc: new Date(session.DateCreatedUtc),
                    DateLastTimeQueriedUtc: new Date(session.DateLastTimeQueriedUtc),
                    CollectionId: session.CollectionId
                }));

                const orderedSessions = reorderSessionsEfficiently(userSessions);
                setSessions(orderedSessions);

                if (hasUserSelectedSession && userSelectedSessionId) {
                    setCurrentSessionId(userSelectedSessionId);
                    setCurrentSessionCollectionId(orderedSessions.find(s => s.SessionId === userSelectedSessionId)?.CollectionId || null);
                }
                // Only auto-select if user hasn't explicitly selected a session
                else if (!currentSessionId && orderedSessions.length > 0) {
                    setCurrentSessionId(orderedSessions[0].SessionId);
                    setCurrentSessionCollectionId(orderedSessions[0].CollectionId || null);
                }

                if (orderedSessions.length === 0) {
                    createNewSession();
                }
            }
        } catch (error) {
            console.error('Error loading user sessions:', error);
            toast.error('Failed to load sessions');
        } finally {
            setIsLoading(false);
        }
    };


    // Function to mark a session as used
    const markSessionAsUsed = async (sessionId: string): Promise<void> => {

        // Mark this session as having messages (since user just sent one)
        setSessionsWithMessages(prev => new Set([...Array.from(prev), sessionId]));

        // Check if session is already at the top of usage order
        if (sessionUsageOrder[0] === sessionId) {
            return;
        }

        // Update usage order - move this session to the front
        setSessionUsageOrder(prev => {
            const filtered = prev.filter(id => id !== sessionId);
            const newOrder = [sessionId, ...filtered];
            return newOrder;
        });
    };

    const toggleSessionSidebar = () => {
        setIsSessionSidebarOpen(!isSessionSidebarOpen);
    };

    const createNewSession = async (title?: string): Promise<string | null> => {
        const userEmail = user?.email;
        if (!userEmail) {
            toast.error('User email not found');
            return null;
        }

        if (sessions.length > 0) {
            const lastSession = sessions[0];
            const hasMessages = sessionLikelyHasMessages(lastSession);

            if (!hasMessages) {
                toast.warning('Please add messages to the current conversation before creating a new one');
                return null;
            }
        }

        const sessionName = title || `New Conversation ${sessions.length + 1}`;
        const newSessionRequest: UserSessionRequest = {
            Name: sessionName,
            UserEmail: userEmail,
            SessionId: '',
            CollectionId: null
        }

        setIsLoading(true);
        try {
            const response = await addUserSession(newSessionRequest);
            if (response?.data) {
                const newSession = {
                    Name: newSessionRequest.Name,
                    UserEmail: newSessionRequest.UserEmail,
                    SessionId: response.data.result,
                    DateCreatedUtc: new Date(),
                    DateLastTimeQueriedUtc: new Date(),
                    CollectionId: null
                };

                setSessions(prev => [newSession, ...prev]);
                setCurrentSessionId(newSession.SessionId);
                setHasUserSelectedSession(true);
                setUserSelectedSessionId(newSession.SessionId);
                setCurrentSessionCollectionId(newSession.CollectionId || null);
                toast.success('New conversation created');
                return newSession.SessionId;
            }
        } catch (error) {
            toast.error('Failed to create new conversation');
        } finally {
            setIsLoading(false);
        }

        return null;
    };

    const updateSession = async (sessionId: string, name: string): Promise<void> => {
        const userEmail = user?.email;
        if (!userEmail) {
            toast.error('User email not found');
            return;
        }

        const updateRequest = new UserSessionRequest(name, userEmail, sessionId, currentSessionCollectionId);

        try {
            const response = await updateUserSession(updateRequest);

            if (response?.data) {
                setSessions(prev => prev.map(session =>
                    session.SessionId === sessionId
                        ? { ...session, Name: name, CollectionId: currentSessionCollectionId }
                        : session
                ));
                toast.success('Session name updated');
            } else {
                toast.error('Failed to update session name - no response data');
            }
        } catch (error) {
            console.error('Error updating session:', error);
            toast.error('Failed to update session name');
        }
    };

    const updateSessionCollectionId = async (sessionId: string, collectionId: number): Promise<void> => {
        const userEmail = user?.email;
        if (!userEmail) {
            toast.error('User email not found');
            return;
        }

        const updateRequest = new UserSessionRequest("", userEmail, sessionId, collectionId);
        try {
            const response = await updateUserSessionCollection(updateRequest);
            if (response?.result) {
                setSessions(prev => prev.map(session =>
                    session.SessionId === sessionId
                        ? { ...session, CollectionId: collectionId }
                        : session
                ));

                setCurrentSessionCollectionId(collectionId);
            }
        } catch (error) {
            console.error('Error updating session collection:', error);
            toast.error('Failed to update session collection');
        }
    };

    const deleteSession = async (sessionId: string) => {
        if (sessions.length <= 1) {
            toast.warning("Can't delete the last conversation");
            return;
        }

        try {
            const response = await deleteUserSession(user?.email || '', sessionId);
            if (response?.data?.result) {
                setSessions(prev => prev.filter(s => s.SessionId !== sessionId));

                setSessionUsageOrder(prev => prev.filter(id => id !== sessionId));

                if (currentSessionId === sessionId) {
                    const remainingSessions = sessions.filter(s => s.SessionId !== sessionId);
                    setCurrentSessionId(remainingSessions[0]?.SessionId || null);
                    setCurrentSessionCollectionId(remainingSessions[0]?.CollectionId || null);
                    setHasUserSelectedSession(false);
                    setUserSelectedSessionId(null);
                }
                toast.success('Conversation deleted successfully');
            }
        } catch (error) {
            console.error('Error deleting session:', error);
            toast.error('Failed to delete conversation');
        }
    };

    const refreshSessions = async () => {
        if (user?.email && isTokenSynced) {
            await loadUserSessions();
        }
    };

    const handleSetCurrentSessionId = (id: string) => {
        setCurrentSessionId(id);
        setHasUserSelectedSession(true); // Mark that the user has explicitly selected a session
        setUserSelectedSessionId(id); // Store the explicitly selected session ID
    };

    const value: SessionContextType = {
        sessions,
        currentSessionId,
        isSessionSidebarOpen,
        isLoading,
        currentSessionCollectionId,
        setCurrentSessionId: handleSetCurrentSessionId,
        setCurrentSessionCollectionId,
        toggleSessionSidebar,
        createNewSession,
        updateSession,
        deleteSession,
        refreshSessions,
        markSessionAsUsed,
        updateSessionCollectionId,
    };

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
}; 